import type { CommentAttachment, CommentMention, CommentReactionsMap } from './index';
import type { PayrollDepartment } from './finances';

export type JobPostingStatus = 'draft' | 'published' | 'closed';

export const JOB_POSTING_STATUSES: JobPostingStatus[] = ['draft', 'published', 'closed'];

export const JOB_POSTING_STATUS_LABELS: Record<JobPostingStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  closed: 'Closed',
};

export type JobEmploymentType = 'full-time' | 'part-time' | 'contract' | 'seasonal';

export const JOB_EMPLOYMENT_TYPES: JobEmploymentType[] = [
  'full-time',
  'part-time',
  'contract',
  'seasonal',
];

export const JOB_EMPLOYMENT_TYPE_LABELS: Record<JobEmploymentType, string> = {
  'full-time': 'Full-time',
  'part-time': 'Part-time',
  contract: 'Contract',
  seasonal: 'Seasonal',
};

export interface JobPosting {
  id: string;
  slug: string;
  title: string;
  department: PayrollDepartment | string;
  location: string;
  employmentType: JobEmploymentType;
  description: string;
  requirements: string;
  status: JobPostingStatus;
  payrollPositionId?: string | null;
  publishedAt?: Date | null;
  closedAt?: Date | null;
  applicationCount: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export type JobApplicationStatus =
  | 'new'
  | 'reviewing'
  | 'interview'
  | 'offered'
  | 'hired'
  | 'rejected';

export const JOB_APPLICATION_STATUSES: JobApplicationStatus[] = [
  'new',
  'reviewing',
  'interview',
  'offered',
  'hired',
  'rejected',
];

export const JOB_APPLICATION_STATUS_LABELS: Record<JobApplicationStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  interview: 'Interview',
  offered: 'Offered',
  hired: 'Hired',
  rejected: 'Rejected',
};

export interface JobApplicationResume {
  fileName: string;
  storagePath: string;
  contentType: string;
  sizeBytes: number;
}

export interface JobApplication {
  id: string;
  jobPostingId: string;
  jobSlug: string;
  jobTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  statement: string;
  /** Applicant answer to "Where did you hear from us" (website apply). */
  heardFrom?: string;
  resume: JobApplicationResume;
  status: JobApplicationStatus;
  source: 'website' | 'manual';
  payrollPositionId?: string | null;
  submittedAt: Date;
  reviewedAt?: Date | null;
  reviewedBy?: string | null;
  internalNotes?: string;
}

export interface JobApplicationComment {
  id: string;
  applicationId: string;
  userId: string;
  userName: string;
  userInitials?: string;
  text: string;
  bodyHtml?: string;
  bodyFormat?: 'html' | 'plain';
  mentions?: CommentMention[];
  attachments?: CommentAttachment[];
  parentId?: string | null;
  reactions?: CommentReactionsMap;
  createdAt: Date;
  updatedAt?: Date;
}

export type JobOfferPayType = 'salary' | 'hourly';

export type JobOfferLetterStatus = 'draft' | 'sent' | 'signed' | 'voided';

export const JOB_OFFER_CLAUSE_IDS = [
  'travelInstall',
  'salaryNoOvertime',
  'hourlyOvertime',
  'profitShare',
  'sepIra',
  'healthReimbursement',
  'executiveResponsibilities',
  'salesCommission',
] as const;

export type JobOfferClauseId = (typeof JOB_OFFER_CLAUSE_IDS)[number];

export interface JobOfferFields {
  employeeFirstName: string;
  employeeLastName: string;
  employeeEmail: string;
  positionTitle: string;
  duties: string;
  startDate: string;
  payType: JobOfferPayType;
  payAmount: number;
  hoursPerWeek: number;
  employmentType: JobEmploymentType;
  location: string;
  reportingTo: string;
  reportingToUserId: string;
  responseDeadline: string;
  ceoName: string;
  ceoTitle: string;
}

export interface JobOfferClauses {
  travelInstall: boolean;
  salaryNoOvertime: boolean;
  hourlyOvertime: boolean;
  profitSharePercent: number | null;
  sepIraPercent: number | null;
  healthReimbursementMonthly: number | null;
  executiveResponsibilities: boolean;
  executiveResponsibilitiesText: string;
  /** Percent of the timber-frame purchase price paid per frame sold. Null when the clause is off. */
  salesCommission: boolean;
  salesCommissionPercent: number | null;
}

export interface JobOfferSection {
  id: string;
  heading: string;
  body: string;
}

export interface JobOfferSignature {
  dataUrl: string;
  signedBy: string;
  signedAt: Date;
  title?: string;
  ipAddress?: string;
  userAgent?: string;
  source?: string;
  grandfathered?: boolean;
}

export interface JobOfferPolicyAck {
  policyId: string;
  policyTitle: string;
  version: string;
  acknowledgedAt: Date;
}

export interface JobOfferLetter {
  id: string;
  applicationId: string;
  slug: string;
  status: JobOfferLetterStatus;
  fields: JobOfferFields;
  clauses: JobOfferClauses;
  sections: JobOfferSection[];
  agreementSections: JobOfferSection[];
  ceoSignature: JobOfferSignature | null;
  employeeSignature: JobOfferSignature | null;
  agreementCeoSignature: JobOfferSignature | null;
  agreementEmployeeSignature: JobOfferSignature | null;
  policyAcks: JobOfferPolicyAck[];
  startDateConfirmedAt?: Date | null;
  sentAt?: Date | null;
  contractSentAt?: Date | null;
  offerSignedAt?: Date | null;
  signedAt?: Date | null;
  signedPdfUrl?: string | null;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export type CandidatePipelineStepId =
  | 'applied'
  | 'reviewed'
  | 'decision'
  | 'offerLetter'
  | 'agreement'
  | 'onboarding'
  | 'startDate';

export type CandidatePipelineStepState = 'complete' | 'current' | 'upcoming' | 'locked' | 'rejected';

export interface CandidatePipelineStep {
  id: CandidatePipelineStepId;
  label: string;
  state: CandidatePipelineStepState;
}

/** Public list item returned by listPublishedJobs. */
export interface PublishedJobListItem {
  slug: string;
  title: string;
  department: string;
  location: string;
  employmentType: JobEmploymentType;
  publishedAt: string;
}
